import axios from 'axios';

const API_URL = process.env.REACT_APP_STRAPI_API_URL || 'https://www.eatmeatapp.com';
const TOKEN_KEY = 'admin_portal_token';
const ME_KEY = 'admin_portal_me';

export const getAdminToken = (): string | null => localStorage.getItem(TOKEN_KEY);
export const getCachedMe = (): AdminMe | null => {
  const raw = localStorage.getItem(ME_KEY);
  return raw ? JSON.parse(raw) : null;
};
export const isAdminPortalLoggedIn = (): boolean => !!getAdminToken();
export const logoutAdminPortal = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(ME_KEY);
};

const authHeaders = () => {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export interface AdminMe {
  id: number;
  firstname: string | null;
  lastname: string | null;
  email: string;
  username: string | null;
  roles: { id: number; code: string; name: string }[];
  roleCodes: string[];
  isSuperAdmin: boolean;
}

// Strapi admin login lives at /admin/login on the same host. We call it through
// the public origin so cookies/cors are not a concern (Strapi admin issues a JWT
// in the response body).
export const adminPortalLogin = async (email: string, password: string): Promise<AdminMe> => {
  const res = await axios.post(`${API_URL}/admin/login`, { email, password });
  // Strapi returns { data: { token, user } }
  const token = res.data?.data?.token;
  if (!token) throw new Error('No token in response');
  localStorage.setItem(TOKEN_KEY, token);
  const me = await adminPortalMe();
  localStorage.setItem(ME_KEY, JSON.stringify(me));
  return me;
};

export const adminPortalMe = async (): Promise<AdminMe> => {
  const res = await axios.get(`${API_URL}/api/admin-portal/me`, { headers: authHeaders() });
  return res.data;
};

export interface DashboardSummary {
  users: { total: number; premium: number; new_30d: number; banned: number };
  community: { pending_posts: number };
  referrals: { total: number };
}
export interface RecentSignup {
  id: number;
  username: string | null;
  email: string;
  confirmed: boolean;
  is_premium: boolean;
  createdAt: string;
}
export const fetchRecentSignups = async (limit = 10): Promise<{ data: RecentSignup[] }> => {
  const res = await axios.get(`${API_URL}/api/admin-portal/recent-signups`, {
    params: { limit }, headers: authHeaders(),
  });
  return res.data;
};

export interface SubscriptionRow {
  id: number;
  platform_origin: string | null;
  subscription_status: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  apple_product_id: string | null;
  updatedAt: string;
  user: { id: number; username: string | null; email: string } | null;
}
export const fetchSubscriptions = async (status = '', page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/subscriptions`, {
    params: { status, page, pageSize }, headers: authHeaders(),
  });
  return res.data as { data: SubscriptionRow[]; counts: any; pagination: any };
};

export interface AppSettings {
  id?: number;
  autoApproveCommunityPosts?: boolean;
  FeatureFoodChoices?: string | null;
  FeatureHealthAndDietTracker?: string | null;
  ChooseMultipleOptionsText?: string | null;
  DietaryChoice?: string | null;
  CarnivoreDetails?: string | null;
  KetovoreDetails?: string | null;
  iosAppLink?: string | null;
  androidAppLink?: string | null;
  heroTitle?: string | null;
  heroSubtitle?: string | null;
  ctaTitle?: string | null;
  ctaSubtitle?: string | null;
  promoText?: string | null;
  communityHeaderCarnivore?: string | null;
  communityHeaderKetovore?: string | null;
  communitySubheaderCarnivore?: string | null;
  communitySubheaderKetovore?: string | null;
}
export const fetchAppSettings = async (): Promise<{ data: AppSettings | null }> => {
  const res = await axios.get(`${API_URL}/api/admin-portal/settings`, { headers: authHeaders() });
  return res.data;
};
export const updateAppSettings = async (payload: Partial<AppSettings>) => {
  const res = await axios.put(`${API_URL}/api/admin-portal/settings`, payload, { headers: authHeaders() });
  return res.data;
};

export interface RecipeRow {
  id: number;
  documentId: string;
  title: string;
  cooking_time: string | null;
  servings: number | null;
  description: string;
  thumbs_up_count: number;
  thumbs_down_count: number;
  publishedAt: string | null;
  updatedAt: string;
  image_url: string | null;
  category: { id: number; name: string } | null;
}
export const fetchRecipes = async (q = '', page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/recipes`, {
    params: { q, page, pageSize }, headers: authHeaders(),
  });
  return res.data as { data: RecipeRow[]; pagination: any };
};

export interface RecipeIngredient { id: number; name: string | null; quantity: string | null; unit: string | null; notes: string | null; isReplaceable: boolean; }
export interface RecipeNutrient { id: number; label: string | null; value: string | null; }
export interface RecipeDetail extends RecipeRow {
  instructions: any; // Strapi blocks
  ingredients: RecipeIngredient[];
  nutrients: RecipeNutrient[];
  subcategory: { id: number; name: string } | null;
}
export const fetchRecipeDetail = async (id: number): Promise<RecipeDetail> => {
  const res = await axios.get(`${API_URL}/api/admin-portal/recipes/${id}`, { headers: authHeaders() });
  return res.data;
};
export const unpublishRecipe = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/recipes/${id}/unpublish`, {}, { headers: authHeaders() });
  return res.data;
};
export const publishRecipe = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/recipes/${id}/publish`, {}, { headers: authHeaders() });
  return res.data;
};

export interface PlanRow {
  id: number;
  documentId: string;
  name: string | null;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  apple_product_id: string | null;
  amount: number | null;
  currency: string | null;
  duration: 'monthly' | 'quarterly' | 'half-yearly' | 'yearly' | null;
  is_active: boolean;
  features: any;
}
export const fetchPlans = async () => {
  const res = await axios.get(`${API_URL}/api/admin-portal/plans`, { headers: authHeaders() });
  return res.data as { data: PlanRow[] };
};
export const updatePlan = async (id: number, payload: Partial<PlanRow>) => {
  const res = await axios.put(`${API_URL}/api/admin-portal/plans/${id}`, payload, { headers: authHeaders() });
  return res.data;
};

export const fetchBulkEmailPreview = async (audience: string) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/bulk-email/preview`, {
    params: { audience }, headers: authHeaders(),
  });
  return res.data as { audience: string; total: number; sample: { id: number; email: string; username: string | null }[] };
};
export const sendBulkEmail = async (payload: { subject: string; htmlBody?: string; textBody?: string; audience: string; confirmText: string }) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/bulk-email/send`, payload, { headers: authHeaders() });
  return res.data as { sent: number; failed: number; total: number; errors?: any[] };
};

export interface AnalyticsBundle {
  users: { total: number; premium: number; free: number; confirmed: number; unconfirmed: number; banned: number; new30: number; new7: number };
  devices: { ios: number; android: number; unknown: number };
  subscriptions: { active: number; past_due: number; canceled: number; expired: number };
  community: { pending: number; live: number; hidden: number; total_comments: number };
  referrals: { total: number; active: number; revenue_30d: number; commission_30d: number };
  trends: { daily_signups: Record<string, number>; daily_paid_conversions: Record<string, number> };
}
export const fetchAnalytics = async () => {
  const res = await axios.get(`${API_URL}/api/admin-portal/analytics`, { headers: authHeaders() });
  return res.data as AnalyticsBundle;
};

export const fetchAdminDashboard = async (): Promise<DashboardSummary> => {
  const res = await axios.get(`${API_URL}/api/admin-portal/dashboard`, { headers: authHeaders() });
  return res.data;
};

export interface SearchedUser {
  id: number;
  username: string | null;
  email: string;
  confirmed: boolean;
  blocked: boolean;
  is_premium: boolean;
  subscription_status: string | null;
  community_badge: string | null;
  is_community_moderator: boolean;
  is_community_banned: boolean;
  referral_code_used: string | null;
  createdAt: string;
  updatedAt: string;
}

export const searchUsers = async (q: string, page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/users`, {
    params: { q, page, pageSize },
    headers: authHeaders(),
  });
  return res.data as { data: SearchedUser[]; pagination: any };
};

export const fetchUserDetail = async (id: number) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/users/${id}`, { headers: authHeaders() });
  return res.data;
};

export const banUser = async (id: number, reason: string, days?: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/ban`,
    { reason, days }, { headers: authHeaders() });
  return res.data;
};

export const unbanUser = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/unban`, {}, { headers: authHeaders() });
  return res.data;
};

export const setUserBadge = async (id: number, badge: string) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/community-badge`,
    { badge }, { headers: authHeaders() });
  return res.data;
};

export const blockUser = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/block`, {}, { headers: authHeaders() });
  return res.data;
};
export const unblockUser = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/unblock`, {}, { headers: authHeaders() });
  return res.data;
};
export const sendUserPasswordReset = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/send-password-reset`, {}, { headers: authHeaders() });
  return res.data;
};
export const resendUserConfirmation = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/resend-confirmation`, {}, { headers: authHeaders() });
  return res.data;
};

export const setUserModerator = async (id: number, value: boolean) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/users/${id}/moderator`,
    { value }, { headers: authHeaders() });
  return res.data;
};

export const fetchAllCommunityPosts = async (q = '', page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/all`, {
    params: { q, page, pageSize }, headers: authHeaders(),
  });
  return res.data;
};

export const fetchPendingCommunityPosts = async (page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/pending`, {
    params: { page, pageSize },
    headers: authHeaders(),
  });
  return res.data;
};

export const fetchReportedCommunityPosts = async (page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/reported`, {
    params: { page, pageSize }, headers: authHeaders(),
  });
  return res.data;
};

export const fetchHiddenCommunityPosts = async (page = 1, pageSize = 20) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/hidden`, {
    params: { page, pageSize }, headers: authHeaders(),
  });
  return res.data;
};

export const approvePost = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/posts/${id}/approve`, {}, { headers: authHeaders() });
  return res.data;
};
export const hidePost = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/posts/${id}/hide`, {}, { headers: authHeaders() });
  return res.data;
};
export const unhidePost = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/posts/${id}/unhide`, {}, { headers: authHeaders() });
  return res.data;
};
export const clearPostReports = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/posts/${id}/clear-reports`, {}, { headers: authHeaders() });
  return res.data;
};
export const deletePost = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/posts/${id}/delete`, {}, { headers: authHeaders() });
  return res.data;
};

// Comments
export const fetchCommunityComments = async (filter: 'reported' | 'hidden' | 'all' = 'reported', page = 1, pageSize = 30) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/comments`, {
    params: { filter, page, pageSize }, headers: authHeaders(),
  });
  return res.data;
};
export const hideComment = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/comments/${id}/hide`, {}, { headers: authHeaders() });
  return res.data;
};
export const unhideComment = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/comments/${id}/unhide`, {}, { headers: authHeaders() });
  return res.data;
};
export const deleteComment = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/comments/${id}/delete`, {}, { headers: authHeaders() });
  return res.data;
};
export const clearCommentReports = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/comments/${id}/clear-reports`, {}, { headers: authHeaders() });
  return res.data;
};

// Categories
export interface CommunityCategoryRow {
  id: number;
  documentId: string;
  name: string;
  slug: string | null;
  icon: string | null;
  description: string | null;
  is_active: boolean;
  sortOrder: number;
}
export const fetchCommunityCategories = async () => {
  const res = await axios.get(`${API_URL}/api/admin-portal/community/categories`, { headers: authHeaders() });
  return res.data as { data: CommunityCategoryRow[] };
};
export const createCommunityCategory = async (payload: Partial<CommunityCategoryRow>) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/community/categories`, payload, { headers: authHeaders() });
  return res.data;
};
export const updateCommunityCategory = async (id: number, payload: Partial<CommunityCategoryRow>) => {
  const res = await axios.put(`${API_URL}/api/admin-portal/community/categories/${id}`, payload, { headers: authHeaders() });
  return res.data;
};
export const deleteCommunityCategory = async (id: number) => {
  const res = await axios.delete(`${API_URL}/api/admin-portal/community/categories/${id}`, { headers: authHeaders() });
  return res.data;
};

export const fetchReferralsList = async () => {
  const res = await axios.get(`${API_URL}/api/admin-portal/referrals`, { headers: authHeaders() });
  return res.data;
};

export const fetchReferralDetail = async (id: number) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/referrals/${id}`, { headers: authHeaders() });
  return res.data;
};

export interface PayoutPayload {
  amount: number;
  note?: string;
  reference?: string;
  method?: string;
  paid_at?: string;
}
export const recordReferralPayout = async (id: number, payload: PayoutPayload) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/referrals/${id}/payout`, payload, { headers: authHeaders() });
  return res.data;
};

export const deleteReferralPayout = async (refId: number, payoutId: number) => {
  const res = await axios.delete(`${API_URL}/api/admin-portal/referrals/${refId}/payouts/${payoutId}`, { headers: authHeaders() });
  return res.data;
};

export const fetchRefundsList = async (page = 1, pageSize = 30) => {
  const res = await axios.get(`${API_URL}/api/admin-portal/refunds`, {
    params: { page, pageSize }, headers: authHeaders(),
  });
  return res.data;
};

export const fetchReferralApplications = async () => {
  const res = await axios.get(`${API_URL}/api/admin-portal/referrals/applications`, { headers: authHeaders() });
  return res.data;
};

export const approveReferralApplication = async (id: number, payload: { commission_rate?: number; code?: string }) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/referrals/${id}/approve`, payload, { headers: authHeaders() });
  return res.data;
};

export const rejectReferralApplication = async (id: number, reason: string) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/referrals/${id}/reject`, { reason }, { headers: authHeaders() });
  return res.data;
};

export const triggerInfluencerPasswordReset = async (id: number) => {
  const res = await axios.post(`${API_URL}/api/admin-portal/referrals/${id}/password-reset`, {}, { headers: authHeaders() });
  return res.data;
};
